import * as React from 'react';
import {transparentize} from 'polished';
import styled from 'styled-components';
import {Prices} from '../components';
import {IconFlagStart, IconHourglass} from 'icons';
import Button, {ButtonVariant} from 'components/Button';
import {
  getFormattedDate,
  getDiscountPercent,
} from '../helpers';
import {CoursePromoContext} from '../context';
import {DateFormat} from '../types';

const Box = styled.div`
  border-radius: 10px;
  background: ${({theme}) => `
    ${theme.variables.backgroundColor};
  `};
`;

const SmallCard = styled(Box)`
  box-sizing: border-box;
  max-width: 380px;
  padding: 20px;
  h4 {
    margin-top: 18px;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: ${({theme}) => `
      ${transparentize(.2, theme.colors.white)};
    `};
  }
  span {
    font-size: 22px;
    font-weight: 600;
    line-height: 29px;
  }
  svg {
    width: 30px;
    height: 30px;
    fill: ${({theme}) => `
      ${transparentize(.8, theme.colors.white)};
    `};
  }
`;

const Column = styled.div`
  display: grid;
  gap: 20px;
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  padding: 0 60px;
  width: 100%;
  max-width: 1160px;

  @media screen and (max-width: 1024px) {
    padding: 0 16px;
    grid-template-columns: 100%;
    
    & > ${Column} {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      width: 100%;
      max-width: 100%;
      
      ${SmallCard} {
        max-width: none;
      } 
    }
  }

  @media screen and (max-width: 640px) {
    & > ${Column} {
      grid-template-columns: 1fr;
    }
  }
`;

const Card = styled(Box)`
  box-sizing: border-box;
  padding: 50px 40px 50px 50px;
  max-width: 760px;

  @media screen and (max-width: 1024px) {
    max-width: none;
    padding: 20px;
  }
`;

const CardInfo = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 10px;
  margin-top: 50px;

  @media screen and (max-width: 468px) {
    grid-template-columns: 1fr;
  }
`;

const Discount = styled.div`
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: repeat(3, max-content);
  text-align: center;
  gap: 2px;
  color: #29292B;
  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;

    @media screen and (max-width: 1024px) {
      font-size: 12px;
      line-height: 16px;
    }
  }
`;

const DiscountValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 43px;

  @media screen and (max-width: 1024px) {
    font-size: 24px;
    line-height: 29px;
  }
`;

const DiscountBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 150px;
  
  background-image: url("data:image/svg+xml,%3Csvg width='150' height='150' viewBox='0 0 150 150' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M72.1413 0.910562C73.8538 -0.303522 76.1462 -0.30352 77.8587 0.910564L90.6449 9.97583C91.6091 10.6594 92.7864 10.9749 93.9632 10.865L109.569 9.40733C111.659 9.21212 113.644 10.3584 114.52 12.266L121.061 26.5099C121.554 27.584 122.416 28.4458 123.49 28.939L137.734 35.4796C139.642 36.3556 140.788 38.3409 140.593 40.431L139.135 56.0368C139.025 57.2136 139.341 58.3909 140.024 59.3551L149.089 72.1413C150.304 73.8538 150.304 76.1463 149.089 77.8587L140.024 90.6449C139.341 91.6091 139.025 92.7864 139.135 93.9632L140.593 109.569C140.788 111.659 139.642 113.644 137.734 114.52L123.49 121.061C122.416 121.554 121.554 122.416 121.061 123.49L114.52 137.734C113.644 139.642 111.659 140.788 109.569 140.593L93.9632 139.135C92.7864 139.025 91.6091 139.341 90.6449 140.024L77.8587 149.089C76.1462 150.304 73.8538 150.304 72.1413 149.089L59.3551 140.024C58.3909 139.341 57.2136 139.025 56.0368 139.135L40.431 140.593C38.3409 140.788 36.3556 139.642 35.4796 137.734L28.939 123.49C28.4458 122.416 27.584 121.554 26.5099 121.061L12.266 114.52C10.3584 113.644 9.21212 111.659 9.40733 109.569L10.865 93.9632C10.9749 92.7864 10.6594 91.6091 9.97583 90.6449L0.910562 77.8587C-0.303522 76.1462 -0.30352 73.8538 0.910564 72.1413L9.97583 59.3551C10.6594 58.3909 10.9749 57.2136 10.865 56.0368L9.40733 40.431C9.21212 38.3409 10.3584 36.3556 12.266 35.4796L26.5099 28.939C27.584 28.4458 28.4458 27.584 28.939 26.5099L35.4796 12.266C36.3556 10.3584 38.3409 9.21212 40.431 9.40733L56.0368 10.865C57.2136 10.9749 58.3909 10.6594 59.3551 9.97583L72.1413 0.910562Z' fill='%23FFE605'/%3E%3C/svg%3E%0A");
  background-size: 100%;
  background-position: 50%;
  background-repeat: no-repeat;

  @media screen and (max-width: 1024px) {
    width: 110px;
    height: 110px;
    min-width: 110px;
    min-height: 110px;
  }
`;

const Title = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 43px;
  margin-bottom: 30px;
`;

/**
 * The returns course cost component
 * @return {React.ReactElement}
 * @constructor
 */
export default function Cost(): React.ReactElement {
  const context = React.useContext(CoursePromoContext);
  const {dates, daysAccessCount} = context;
  const {old, current, discountEndDate} = context.prices;
  const discountPercent = old && getDiscountPercent(old, current);

  /**
   * The render cost
   * @return {JSX.Element}
   * @constructor
   */
  function renderCost():JSX.Element {
    return (
      <Column>
        <Title>Стоимость обучения</Title>
        <Prices />
      </Column>
    );
  }

  /**
   * The returns course discount percent component
   * @return {JSX.Element | undefined}
   * @constructor
   */
  function renderDiscount():JSX.Element | undefined {
    if (discountPercent) {
      return (
        <DiscountBox>
          <Discount>
            <span>Скидка</span>
            <DiscountValue>
              {discountPercent}%
            </DiscountValue>
            {renderDiscountEnd()}
          </Discount>
        </DiscountBox>
      );
    }
  }

  /**
   * The returns course discount percent component
   * @return {JSX.Element | undefined}
   * @constructor
   */
  function renderDiscountEnd():JSX.Element | undefined {
    if (discountEndDate) {
      const date = getFormattedDate(discountEndDate, DateFormat.DayMonth);
      return (
        <span>до {date}</span>
      );
    }
  }

  /**
   * The returns course start card
   * @return {JSX.Element}
   * @constructor
   */
  function renderCourseStart():JSX.Element {
    const date = getFormattedDate(dates.start, DateFormat.Date);
    return (
      <SmallCard>
        <IconFlagStart />
        <h4>старт</h4>
        <span>{date}</span>
      </SmallCard>
    );
  }

  /**
   * The returns course start card
   * @return {JSX.Element}
   * @constructor
   */
  function renderCourseDuration():JSX.Element {
    const access = daysAccessCount > 0 && ` + доступ ${daysAccessCount} дней`;
    const duration = dates.duration && `${dates.duration} дней`;
    return (
      <SmallCard>
        <IconHourglass />
        <h4>продолжительность</h4>
        <span>
          {duration}
          {access}
        </span>
      </SmallCard>
    );
  }

  return (
    <Container>
      <Card>
        <CardInfo>
          {renderCost()}
          {renderDiscount()}
        </CardInfo>
        <Buttons>
          <Button variant={ButtonVariant.Primary}>Записаться на курс</Button>
          <Button variant={ButtonVariant.Outline}>Купить в рассрочку</Button>
        </Buttons>
      </Card>
      <Column>
        {renderCourseStart()}
        {renderCourseDuration()}
      </Column>
    </Container>
  );
}
